/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { usePhraseLists } from 'src/luis/modules/features/hooks/usePhraseLists';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { ModelIdentifier } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { PredictionStore } from 'src/luis/projects/utterances/stores/predictionStore';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { getUtteranceStoreHouse } from 'src/luis/projects/utterances/utils/utteranceUtils';

export type UtterancePredictionsStore = {
    utteranceStore: UtteranceStore;
    predictionStore?: PredictionStore;
};

/**
 * A hook for subscribing to utterances belonging to a specific model.
 */
export const useUtterances = ({ model, isChunking }: ModelIdentifier & { isChunking: boolean }) => {
    const { dispatcher, rootStore } = useGlobalState();
    const { isLoaded: areIntentsLoaded, isLoading: areIntentsLoading, intents } = useIntents();
    const { isLoaded: areEntitiesLoaded, isLoading: areEntitiesLoading, entities } = useEntities();
    const { isLoaded: arePhraseListsLoaded, isLoading: arePhraseListsLoading } = usePhraseLists();
    const [utterances, setUtterances] = React.useState<UtterancePredictionsStore[]>(null);
    const [utterancesMetadata, setUtterancesMetadata] = React.useState({ pageIndex: 0, totalSize: 0, pageSize: 10 });
    const appVersionStore = rootStore.currentLuisAppStore.currentAppVersionStore;
    const utteranceStoreHouse = getUtteranceStoreHouse(model, appVersionStore);

    const isReadyToFetchUtterances = areEntitiesLoaded && areIntentsLoaded && arePhraseListsLoaded;

    // This flag becomes true when we toggle off chunking and we still have all the utterances (> page size),
    // the moment that `loadUtterancePage` finishes loading, utterances get to be one page again (<= page size)
    // and this condition becomes false.
    const wasChunking = !isChunking && (utterances?.length ?? 0) > utterancesMetadata.pageSize;

    React.useEffect(() => {
        if (isReadyToFetchUtterances) {
            if (isChunking) {
                dispatcher.dispatch('loadUtterancesByChunking', { model });
            } else {
                dispatcher.dispatch('cancelUtterancesChunking', { model });
                dispatcher.dispatch('loadUtterancePage', { model, context: { forceRefresh: true } });
            }
        }
    }, [dispatcher.dispatch, isReadyToFetchUtterances, model.id, model.typeId, isChunking]);

    React.useEffect(
        () =>
            autorun(() => {
                /**
                 * The check for the current app store is to counter a React
                 * bug behavior where the parent useEffect is disposed before
                 * children useEffects, which means that the app is marked as
                 * closed before the children can clean up their disposers. It
                 * is very stupid and there is a discussion on GitHub on this
                 * issue. Upgrading to React 17 may potentially solve the problem.
                 */
                if (!isReadyToFetchUtterances || !rootStore.currentAppStore) {
                    return;
                }

                if (utteranceStoreHouse.activePage === undefined) {
                    return;
                }

                if (!isChunking) {
                    dispatcher.dispatch('loadPredictions', {
                        utterances: [...utteranceStoreHouse.activePage.values()].map(u => u.text)
                    });
                }

                const { predictions } = appVersionStore.predictionStoreHouse;

                const stores = isChunking ? [...utteranceStoreHouse.allPages] : [...utteranceStoreHouse.activePage.values()];
                setUtterances(
                    stores.map(store => {
                        const prediction = predictions.get(store.text);

                        if (!prediction) {
                            return { utteranceStore: store };
                        }

                        return {
                            utteranceStore: store,
                            predictionStore: prediction
                        };
                    })
                );
            }),
        [model.id, model.typeId, isReadyToFetchUtterances, isChunking]
    );

    React.useEffect(
        () =>
            autorun(() => {
                if (!isReadyToFetchUtterances) {
                    return;
                }

                setUtterancesMetadata({
                    pageIndex: utteranceStoreHouse.activePageIndex,
                    totalSize: isChunking ? utteranceStoreHouse.allPages.length : utteranceStoreHouse.totalSize,
                    pageSize: utteranceStoreHouse.pageSize
                });
            }),
        [isReadyToFetchUtterances, isChunking]
    );

    return {
        intents,
        entities,
        utterances: utterances ?? [],
        isLoading: areEntitiesLoading || areIntentsLoading || arePhraseListsLoading || utterances === null || wasChunking,
        isLoadingChunks: utteranceStoreHouse?.isChunking,
        areModelsLoaded: isReadyToFetchUtterances,
        ...utterancesMetadata
    };
};
